<template>
  <div class="dashboard-eco-impact" id="eco-impact-section">
    <div class="eco-header">
      <h3 class="eco-title">
        <v-icon small color="#059669">mdi-leaf</v-icon>
        {{ $t('org-dashboard.eco-impact') }}
      </h3>
      <div class="eco-subtitle">{{ $t('org-dashboard.section-esg-subtitle') }}</div>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="eco-loading">
      <v-skeleton-loader type="card" />
    </div>

    <!-- Empty state -->
    <div v-else-if="!data" class="eco-empty">
      <v-icon color="grey lighten-1" size="40">mdi-leaf</v-icon>
      <p>{{ $t('org-dashboard.peak-no-data') }}</p>
    </div>

    <template v-else>
      <!-- Hero KPIs -->
      <div class="eco-hero-grid">
        <div class="eco-hero-card eco-hero-card--green">
          <v-icon color="#059669" size="20" class="eco-hero-icon">mdi-molecule-co2</v-icon>
          <div class="eco-hero-value">{{ formatTons(eco.co2SavedTons) }} <span class="eco-hero-unit">t</span></div>
          <div class="eco-hero-label">{{ $t('org-dashboard.eco-co2-saved') }}</div>
          <div v-if="ecoChange != null" class="eco-hero-change" :class="ecoChange >= 0 ? 'eco-hero-change--up' : 'eco-hero-change--down'">
            {{ ecoChange >= 0 ? '+' : '' }}{{ ecoChange }}%
          </div>
        </div>
        <div class="eco-hero-card">
          <v-icon color="#6B7280" size="20" class="eco-hero-icon">mdi-car</v-icon>
          <div class="eco-hero-value">{{ formatNumber(eco.estimatedKm) }}</div>
          <div class="eco-hero-label">{{ $t('org-dashboard.eco-km') }}</div>
        </div>
        <div class="eco-hero-card">
          <v-icon color="#6B7280" size="20" class="eco-hero-icon">mdi-account-group</v-icon>
          <div class="eco-hero-value">{{ eco.activeDrivers || 0 }}</div>
          <div class="eco-hero-label">{{ $t('org-dashboard.active-users') }}</div>
        </div>
      </div>

      <!-- Narrative insight -->
      <div v-if="narrativeInsight" class="eco-narrative">
        <v-icon small color="#059669" class="mr-1">mdi-information-outline</v-icon>
        <span v-html="narrativeInsight"></span>
      </div>

      <!-- Equivalents -->
      <div class="eco-equiv-grid">
        <div class="eco-equiv-card eco-equiv-card--green">
          <v-icon color="#059669" size="28">mdi-tree</v-icon>
          <div class="eco-equiv-value">{{ formatNumber(equiv.trees) }}</div>
          <div class="eco-equiv-label">{{ $t('org-dashboard.eco-trees') }}</div>
        </div>
        <div class="eco-equiv-card eco-equiv-card--blue">
          <v-icon color="#3B82F6" size="28">mdi-airplane</v-icon>
          <div class="eco-equiv-value">{{ formatNumber(equiv.flightsParisMarseille) }}</div>
          <div class="eco-equiv-label">{{ $t('org-dashboard.eco-flights') }}</div>
        </div>
        <div class="eco-equiv-card eco-equiv-card--amber">
          <v-icon color="#F59E0B" size="28">mdi-fuel</v-icon>
          <div class="eco-equiv-value">{{ formatNumber(equiv.fuelLitersSaved) }}</div>
          <div class="eco-equiv-label">{{ $t('org-dashboard.eco-fuel') }}</div>
        </div>
      </div>

      <!-- Per-driver averages -->
      <div class="eco-per-driver-grid" v-if="perDriver">
        <div class="eco-mini-kpi">
          <div class="eco-mini-value">{{ formatDecimal(perDriver.co2SavedKg / 1000) }} t</div>
          <div class="eco-mini-label">{{ $t('org-dashboard.eco-per-driver-co2') }}</div>
        </div>
        <div class="eco-mini-kpi">
          <div class="eco-mini-value">{{ formatNumber(perDriver.estimatedKm) }} km</div>
          <div class="eco-mini-label">{{ $t('org-dashboard.eco-per-driver-km') }}</div>
        </div>
        <div class="eco-mini-kpi">
          <div class="eco-mini-value">{{ formatNumber(perDriver.treesEquivalent) }}</div>
          <div class="eco-mini-label">{{ $t('org-dashboard.eco-per-driver-trees') }}</div>
        </div>
      </div>

      <!-- Trend chart -->
      <div class="eco-section" v-if="data.trend && data.trend.length">
        <h4 class="eco-section-title">{{ $t('org-dashboard.eco-trend') }}</h4>
        <highcharts v-if="trendChartOptions" :options="trendChartOptions" />
      </div>

      <!-- Top drivers leaderboard -->
      <div class="eco-section" v-if="!hideLeaderboard && data.topDrivers && data.topDrivers.length">
        <h4 class="eco-section-title">{{ $t('org-dashboard.eco-top-drivers') }}</h4>
        <table class="eco-leaderboard">
          <thead>
            <tr>
              <th>{{ $t('org-dashboard.eco-rank') }}</th>
              <th>{{ $t('org-dashboard.eco-driver') }}</th>
              <th class="text-right">{{ $t('org-dashboard.eco-co2-saved') }}</th>
              <th class="text-right">{{ $t('org-dashboard.eco-trees-equiv') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(driver, i) in data.topDrivers" :key="i">
              <td class="eco-rank">{{ i + 1 }}</td>
              <td>
                <span
                  v-if="driverMemberMap[driver.name]"
                  class="eco-driver-link"
                  @click="$emit('driver-click', driverMemberMap[driver.name])"
                >{{ driver.name }}</span>
                <span v-else>{{ driver.name }}</span>
              </td>
              <td class="text-right eco-value-cell">{{ formatDecimal(driver.co2SavedKg / 1000) }} t</td>
              <td class="text-right eco-value-cell">{{ formatDecimal(driver.treesEquivalent) }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Assumptions footer -->
      <div class="eco-section">
        <v-expansion-panels flat>
          <v-expansion-panel>
            <v-expansion-panel-header class="eco-assumptions-header">
              {{ $t('org-dashboard.eco-assumptions') }}
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <div class="eco-assumptions-content" v-if="assumptions">
                <div class="eco-assumption-item">
                  <span class="eco-assumption-label">{{ $t('org-dashboard.eco-thermal-emission') }}</span>
                  <span class="eco-assumption-value">{{ (assumptions.thermalEmissionKgPerKm * 1000).toFixed(0) }} g CO₂/km</span>
                </div>
                <div class="eco-assumption-item">
                  <span class="eco-assumption-label">{{ $t('org-dashboard.eco-ev-consumption') }}</span>
                  <span class="eco-assumption-value">{{ (assumptions.evConsumptionKwhPerKm * 100).toFixed(1) }} kWh/100km</span>
                </div>
                <div class="eco-assumption-item">
                  <span class="eco-assumption-label">{{ $t('org-dashboard.eco-electric-mix') }}</span>
                  <span class="eco-assumption-value">{{ (assumptions.electricMixKgPerKwh * 1000).toFixed(0) }} g CO₂/kWh</span>
                </div>
              </div>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </div>
    </template>
  </div>
</template>

<script>
import { Chart } from 'highcharts-vue'

export default {
  name: 'DashboardEcoImpact',
  components: {
    highcharts: Chart,
  },
  props: {
    data: { type: Object, default: null },
    loading: { type: Boolean, default: false },
    driverMemberMap: { type: Object, default: () => ({}) },
    hideLeaderboard: { type: Boolean, default: false },
  },

  computed: {
    eco() {
      return this.data?.ecoImpact || {}
    },

    equiv() {
      return this.data?.ecoEquivalents || {}
    },

    perDriver() {
      return this.eco?.perDriver || null
    },

    ecoChange() {
      const v = this.eco?.change ?? this.eco?.changePercent
      return v != null ? v : null
    },

    assumptions() {
      return this.data?.assumptions || null
    },

    narrativeInsight() {
      // Calculé côté serveur, avec les mêmes hypothèses que les bilans PDF.
      const cars = this.equiv?.thermalCars
      if (!cars || cars < 1) return null
      return this.$t('org-dashboard.eco-narrative', { cars })
    },

    trendChartOptions() {
      const trend = this.data?.trend
      if (!trend?.length) return null

      return {
        chart: {
          type: 'column',
          height: 220,
          backgroundColor: 'transparent',
          style: { fontFamily: 'inherit' },
        },
        title: { text: null },
        credits: { enabled: false },
        legend: { enabled: false },
        xAxis: {
          categories: trend.map(t => t.month || t.label || ''),
          labels: { style: { fontSize: '10px', color: '#9ca3af' } },
          lineColor: '#e5e7eb',
        },
        yAxis: {
          title: { text: null },
          labels: { style: { fontSize: '10px', color: '#6b7280' } },
          gridLineColor: '#f1f5f9',
          min: 0,
        },
        tooltip: {
          valueSuffix: ' t CO₂',
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          borderRadius: 8,
          style: { fontSize: '11px' },
        },
        plotOptions: {
          column: {
            borderRadius: 4,
            borderWidth: 0,
            color: '#059669',
          },
          series: { animation: { duration: 600 } },
        },
        series: [{
          name: this.$t('org-dashboard.eco-co2-saved'),
          data: trend.map(t => (t.co2SavedKg || 0) / 1000),
        }],
      }
    },
  },

  methods: {
    formatTons(val) {
      if (val == null) return '-'
      if (val >= 1000) return (val / 1000).toFixed(1) + 'k'
      return val.toFixed(1)
    },

    formatNumber(val) {
      if (val == null) return '-'
      if (typeof val === 'number') return Math.round(val).toLocaleString()
      return val
    },

    formatDecimal(val) {
      if (val == null) return '-'
      return val.toFixed(2)
    },
  },
}
</script>

<style lang="scss" scoped>
.dashboard-eco-impact {
  background: white;
  border-radius: 12px;
  padding: 24px;
  border: 1px solid #e5e7eb;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
}

.eco-header {
  margin-bottom: 20px;
}

.eco-subtitle {
  font-size: 11px;
  color: #9ca3af;
  margin-top: 4px;
  font-weight: 400;
}

.eco-title {
  font-size: 14px;
  font-weight: 700;
  color: #1f2937;
  display: flex;
  align-items: center;
  gap: 6px;
}

.eco-loading, .eco-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  gap: 8px;

  p {
    font-size: 13px;
    color: #9ca3af;
    margin: 0;
  }
}

/* Narrative */
.eco-narrative {
  display: flex;
  align-items: center;
  background: #ecfdf5;
  border: 1px solid #a7f3d0;
  border-radius: 8px;
  padding: 10px 14px;
  margin-bottom: 20px;
  font-size: 13px;
  font-weight: 600;
  color: #065f46;
  line-height: 1.4;
}

/* Hero KPIs */
.eco-hero-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  margin-bottom: 16px;
}

.eco-hero-card {
  background: #f9fafb;
  border-radius: 10px;
  padding: 16px;
  text-align: center;
  border: 1px solid #f3f4f6;
  position: relative;

  &--green {
    background: #ecfdf5;
    border-color: #a7f3d0;
  }
}

.eco-hero-icon {
  margin-bottom: 4px;
}

.eco-hero-value {
  font-size: 24px;
  font-weight: 700;
  color: #1f2937;
  line-height: 1.2;
}

.eco-hero-card--green .eco-hero-value {
  color: #059669;
}

.eco-hero-unit {
  font-size: 14px;
  font-weight: 600;
  opacity: 0.7;
}

.eco-hero-label {
  font-size: 11px;
  color: #6b7280;
  margin-top: 2px;
}

.eco-hero-change {
  position: absolute;
  top: 8px;
  right: 8px;
  font-size: 10px;
  font-weight: 600;
  padding: 1px 6px;
  border-radius: 4px;
  display: inline-block;

  &--up {
    color: #059669;
    background: #d1fae5;
  }

  &--down {
    color: #dc2626;
    background: #fef2f2;
  }
}

/* Equivalents */
.eco-equiv-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  margin-bottom: 20px;
}

.eco-equiv-card {
  border-radius: 10px;
  padding: 16px;
  text-align: center;
  border: 1px solid #f3f4f6;

  &--green {
    background: #f0fdf4;
    border-color: #bbf7d0;
  }

  &--blue {
    background: #eff6ff;
    border-color: #bfdbfe;
  }

  &--amber {
    background: #fffbeb;
    border-color: #fde68a;
  }
}

.eco-equiv-value {
  font-size: 20px;
  font-weight: 700;
  color: #1f2937;
  margin-top: 8px;
}

.eco-equiv-label {
  font-size: 10px;
  color: #6b7280;
  margin-top: 2px;
}

/* Per-driver averages */
.eco-per-driver-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  margin-bottom: 20px;
}

.eco-mini-kpi {
  background: #f9fafb;
  border-radius: 8px;
  padding: 12px;
  text-align: center;
  border: 1px solid #f3f4f6;
}

.eco-mini-value {
  font-size: 16px;
  font-weight: 700;
  color: #1f2937;
}

.eco-mini-label {
  font-size: 10px;
  color: #9ca3af;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  margin-top: 2px;
}

/* Sections */
.eco-section {
  margin-bottom: 20px;

  &:last-child {
    margin-bottom: 0;
  }
}

.eco-section-title {
  font-size: 13px;
  font-weight: 700;
  color: #374151;
  margin-bottom: 12px;
}

/* Leaderboard */
.eco-leaderboard {
  width: 100%;
  border-collapse: collapse;

  thead th {
    font-size: 10px;
    color: #9ca3af;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    font-weight: 500;
    padding: 6px 8px;
    border-bottom: 1px solid #f3f4f6;
  }

  tbody td {
    padding: 8px;
    font-size: 12px;
    border-bottom: 1px solid #f9fafb;
  }
}

.eco-rank {
  font-weight: 700;
  color: #059669;
  width: 30px;
}

.eco-value-cell {
  font-weight: 600;
  color: #374151;
}

.eco-driver-link {
  color: #FF5722;
  cursor: pointer;
  font-weight: 600;

  &:hover {
    text-decoration: underline;
  }
}

/* Assumptions */
.eco-assumptions-header {
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
  min-height: 36px !important;
  padding: 0 !important;
}

.eco-assumptions-content {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.eco-assumption-item {
  display: flex;
  justify-content: space-between;
  font-size: 11px;
  color: #6b7280;
  padding: 4px 0;
}

.eco-assumption-label {
  font-weight: 500;
}

.eco-assumption-value {
  font-weight: 600;
  color: #374151;
}

@media (max-width: 768px) {
  .eco-hero-grid,
  .eco-equiv-grid,
  .eco-per-driver-grid {
    grid-template-columns: 1fr;
  }
}
</style>
